/* ── Shared types, constants & helpers for the AcordSmartScan feature ── */

export const getToolboxToken = () => localStorage.getItem('toolboxToken') || '';
export const getNcToken = () => localStorage.getItem('amsToken') || '';

/* ── Document type ── */
export const DOCUMENT_TYPE_OPTIONS = [
  { value: 'Acord125', label: 'Acord 125' },
  { value: 'Acord126', label: 'Acord 126' },
  { value: 'Acord130', label: 'Acord 130' },
  { value: 'DecPage', label: 'Dec Page' },
  { value: 'QuoteLetter', label: 'Quote Letter' },
];

export const isDecOrQuote = (documentType: string) =>
  documentType === 'DecPage' || documentType === 'QuoteLetter';

/* ── NowCerts billing type mapping (Fees & Taxes tab) ──
 * Mirrors the legacy `BILLING_TYPES` list added to SmartScanLandingPage.tsx
 * in "Add fees/taxes mapping and AMS upload flow" (commit 0f59240e, 2026-07-22).
 * Names are human-readable (no underscores) — this list is specific to
 * AcordSmartScan and is NOT the same list used by InvoiceScan. */
export const BILLING_TYPE_DATA = [
  { name: 'Initial Base Premium', value: 0 },
  { name: 'Positive Endorsement', value: 1 },
  { name: 'Negative Endorsement', value: 2 },
  { name: 'Cancel', value: 5 },
  { name: 'Renewal Premium', value: 6 },
  { name: 'Reinstate Premium', value: 7 },
  { name: 'Govt Discount', value: 9 },
  { name: 'Premium Audit', value: 13 },
  { name: 'General Discount', value: 14 },
  { name: 'Credit', value: 15 },
  { name: 'Installment', value: 200 },
  { name: 'New Policy Premium', value: 210 },
  { name: 'Rewrite Premium', value: 220 },
  { name: 'No Premium Impact', value: 230 },
  { name: 'Other Fees', value: 3 },
  { name: 'Agency Fee', value: 10 },
  { name: 'Risk Management Fee', value: 101 },
  { name: 'Carrier Fee', value: 11 },
  { name: 'Policy Fee', value: 111 },
  { name: 'MGA Fee', value: 12 },
  { name: 'Inspection Fee', value: 20 },
  { name: 'AI Endorsements Fee', value: 21 },
  { name: 'Subscription Fee', value: 16 },
  { name: 'Program Fee', value: 19 },
  { name: 'Taxes', value: 4 },
  { name: 'State Tax', value: 17 },
  { name: 'Surplus Lines Tax', value: 22 },
  { name: 'Miscellaneous Agency Revenue', value: 23 },
  { name: 'Other Tax', value: 18 },
  { name: 'Fire Marshall Tax', value: 181 },
  { name: 'Stamping Fee', value: 24 },
  { name: 'Processing Fee', value: 35 },
  { name: 'Filing Fee', value: 351 },
  { name: 'APTC Tax Credit', value: 45 },
  { name: 'Expense Constant', value: 100 },
  { name: 'Surplus Contribution', value: 150 },
  { name: 'AB TPA Fee', value: 160 },
  { name: 'Independent Agent Broker Fee', value: 170 },
  { name: 'Payment Processing Fee', value: 180 },
  { name: 'Collateral Fee', value: 190 },
  { name: 'MVR Fee', value: 240 },
  { name: 'CC Fee', value: 250 },
  { name: 'Admin Fee', value: 350 },
  { name: 'Surplus Lines Service Charge', value: 352 },
  { name: 'Zenjuries Fee', value: 353 },
  { name: 'Set Up Fee', value: 354 },
  { name: 'Zywave Fee', value: 355 },
  { name: 'Pass-Through Payment', value: 356 },
  { name: 'Write-Off', value: 357 },
  { name: 'State Assessment', value: 358 },
  { name: 'Big Three Fee', value: 359 },
  { name: 'Fin Co Incentive Fee', value: 360 },
  { name: 'Brokerage Fee', value: 370 },
  { name: 'Agency (Agent) Fee', value: 371 },
  { name: 'Other', value: 8 },
];

/** The "Other Fees" billing type — the only mapping whose endorsement-fee
 * entry also carries a free-text `description` (matches legacy `type === 3`). */
export const OTHER_FEES_BILLING_VALUE = 3;

export function findBillingTypeByValue(value?: number) {
  if (value === undefined || value === null) return undefined;
  return BILLING_TYPE_DATA.find((b) => b.value === value);
}

/* ── Types ── */
export interface NowCertsClient { name: string; databaseId: string; eMail?: string; insuredType?: number; }

export interface Policy {
  id: string;
  policyNumber: string;
  carrierName?: string;
  linesOfBusiness?: string[];
  status?: string;
  effectiveDate?: string;
  expirationDate?: string;
  totalPremium?: number;
  isQuote?: boolean;
}

export interface InsuredData {
  businessName: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  policyNumber: string;
  policyPremium: string;
  carrierName: string;
  lineOfBusiness: string;
  quoteNumber: string;
  insuredType: string;
  fees: string;
  taxes: string;
  effectiveDate: string;
  expirationDate: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  naicsCode?: string;
  submissionId?: string;
  fileUrl?: string;
}

export interface CoverageLimitDetails {
  lineOfBusiness?: string;
  [key: string]: any;
}

export interface PropertyItem {
  AddressLine1?: string;
  City?: string;
  State?: string;
  ZipCode?: string;
  PropertyUseCode?: string;
  databaseId?: string;
  [key: string]: any;
}

export interface VehicleItem {
  Make?: string;
  Model?: string;
  Year?: string;
  Vin?: string;
  [key: string]: any;
}

export interface DriverItem {
  FirstName?: string;
  LastName?: string;
  DateOfBirth?: string;
  LicenseNumber?: string;
  [key: string]: any;
}

export interface FeeItem {
  descriptionField?: string;
  descriptionAmount?: string;
  mappingBillingType?: string;
  mappingBillingTypeValue?: number;
  [key: string]: any;
}

export interface SelectedOption {
  SelectProspect: boolean;
  SelectInsured: boolean;
  CreateNewClient: boolean;
  UseExistingInsured: boolean;
  UseExistingProspect: boolean;
  CreateQuote: boolean;
  CreateNewPolicy: boolean;
  CreateNewProspect: boolean;
  ClientName: string;
  InsuredId: string;
  folderId: string;
  fileIds: string[];
  isFileUploadFromAMSParam: boolean;
}

/* ── Coverage & Limits field sets, by line of business ── */
export interface CoverageFieldSet {
  match: string;
  label: string;
  fields: Array<{ key: string; label: string }>;
}

export const COVERAGE_FIELD_SETS: CoverageFieldSet[] = [
  {
    match: 'general liability',
    label: 'General Liability',
    fields: [
      { key: 'limitEachOccurrence', label: 'Each Occurrence' },
      { key: 'limitGeneralAggregate', label: 'General Aggregate' },
      { key: 'limitMedExp', label: 'Medical Expense' },
      { key: 'limitPersonalAndAdvInjury', label: 'Personal & Adv Injury' },
      { key: 'limitProductsCompOpAggregate', label: 'Products/Comp Op Aggregate' },
      { key: 'limitDamageToRentedPremises', label: 'Damage To Rented Premises' },
    ],
  },
  {
    match: 'auto',
    label: 'Auto Mobile Liability',
    fields: [
      { key: 'limitCombinedSingle', label: 'Combined Single Limit' },
      { key: 'limitBodilyInjuryPerPerson', label: 'Bodily Injury (Per Person)' },
      { key: 'limitBodilyInjuryPerAccident', label: 'Bodily Injury (Per Accident)' },
      { key: 'limitPropertyDamage', label: 'Property Damage' },
    ],
  },
  {
    match: 'workers comp',
    label: 'Workers Compensation',
    fields: [
      { key: 'limitEachAccident', label: 'Each Accident' },
      { key: 'limitEAEmployee', label: 'Each Employee' },
      { key: 'limitPolicy', label: 'Policy Limit' },
    ],
  },
  {
    match: 'physical damage',
    label: 'Physical Damage',
    fields: [
      { key: 'comprehensiveCoverage', label: 'Comprehensive Coverage' },
      { key: 'collisionLimit', label: 'Collision Limit' },
    ],
  },
  {
    match: 'cargo',
    label: 'Cargo / Inland Marine',
    fields: [
      { key: 'deductible', label: 'Deductible' },
      { key: 'limit', label: 'Limit' },
      { key: 'commodities', label: 'Commodities' },
    ],
  },
  {
    match: 'inland marine',
    label: 'Cargo / Inland Marine',
    fields: [
      { key: 'deductible', label: 'Deductible' },
      { key: 'limit', label: 'Limit' },
      { key: 'commodities', label: 'Commodities' },
    ],
  },
  {
    match: 'homeowners',
    label: 'Homeowners / Commercial Property',
    fields: [
      { key: 'dwellingLimit', label: 'Dwelling Limit' },
      { key: 'dwellingPremiumAmount', label: 'Dwelling Premium' },
      { key: 'otherStructureLimit', label: 'Other Structure Limit' },
      { key: 'otherStructurePremiumAmount', label: 'Other Structure Premium' },
      { key: 'personalPropertyLimit', label: 'Personal Property Limit' },
      { key: 'personalPropertyPremiumAmount', label: 'Personal Property Premium' },
      { key: 'lossOfUseLimit', label: 'Loss Of Use Limit' },
      { key: 'lossOfUsePremiumAmount', label: 'Loss Of Use Premium' },
      { key: 'personalLiabilityLimit', label: 'Personal Liability Limit' },
      { key: 'personalLiabilityPremiumAmount', label: 'Personal Liability Premium' },
      { key: 'medicalPaymentsLimit', label: 'Medical Payments Limit' },
      { key: 'medicalPaymentsPremiumAmount', label: 'Medical Payments Premium' },
      { key: 'hurricaneDeductible', label: 'Hurricane Deductible' },
      { key: 'hurricanePremiumAmount', label: 'Hurricane Premium' },
      { key: 'windHailDeductible', label: 'Wind/Hail Deductible' },
      { key: 'ordinanceOrLaw', label: 'Ordinance Or Law' },
      { key: 'allOtherPerilsDeductible', label: 'All Other Perils Deductible' },
    ],
  },
  {
    match: 'commercial property',
    label: 'Homeowners / Commercial Property',
    fields: [
      { key: 'dwellingLimit', label: 'Dwelling Limit' },
      { key: 'dwellingPremiumAmount', label: 'Dwelling Premium' },
      { key: 'otherStructureLimit', label: 'Other Structure Limit' },
      { key: 'otherStructurePremiumAmount', label: 'Other Structure Premium' },
      { key: 'personalPropertyLimit', label: 'Personal Property Limit' },
      { key: 'personalPropertyPremiumAmount', label: 'Personal Property Premium' },
      { key: 'lossOfUseLimit', label: 'Loss Of Use Limit' },
      { key: 'lossOfUsePremiumAmount', label: 'Loss Of Use Premium' },
      { key: 'personalLiabilityLimit', label: 'Personal Liability Limit' },
      { key: 'personalLiabilityPremiumAmount', label: 'Personal Liability Premium' },
      { key: 'medicalPaymentsLimit', label: 'Medical Payments Limit' },
      { key: 'medicalPaymentsPremiumAmount', label: 'Medical Payments Premium' },
      { key: 'hurricaneDeductible', label: 'Hurricane Deductible' },
      { key: 'hurricanePremiumAmount', label: 'Hurricane Premium' },
      { key: 'windHailDeductible', label: 'Wind/Hail Deductible' },
      { key: 'ordinanceOrLaw', label: 'Ordinance Or Law' },
      { key: 'allOtherPerilsDeductible', label: 'All Other Perils Deductible' },
    ],
  },
];

export const CYBER_MATCH = 'cyber';

export function getCoverageFieldSet(lineOfBusiness: string): CoverageFieldSet | null {
  const lower = (lineOfBusiness || '').toLowerCase();
  return COVERAGE_FIELD_SETS.find((set) => lower.includes(set.match)) || null;
}

export function getCyberCoverages(details: CoverageLimitDetails | null | undefined): any[] {
  if (!details) return [];
  const candidate = (details as any).cyberCoverages || (details as any).cyberCoverage;
  if (Array.isArray(candidate)) return candidate;
  if (Array.isArray(details)) return details as any[];
  return [];
}

/* ── Formatting helpers ── */
export function formatCurrency(val: string | number | undefined | null): string {
  if (val === undefined || val === null || val === '') return '';
  const num = typeof val === 'number' ? val : parseFloat(String(val).replace(/[$,]/g, ''));
  if (isNaN(num)) return String(val);
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

export function toDateInput(val?: string): string {
  if (!val) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  const dmy = val.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`;
  const mdy = val.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdy) return `${mdy[3]}-${mdy[1].padStart(2, '0')}-${mdy[2].padStart(2, '0')}`;
  try {
    const d = new Date(val);
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  } catch { /* ignore */ }
  return '';
}

export function parseAmount(val?: string): number {
  if (!val) return 0;
  return parseFloat(String(val).replace(/[$,()]/g, '')) || 0;
}

export function findBillingType(name?: string) {
  if (!name) return undefined;
  return BILLING_TYPE_DATA.find((b) => b.name === name);
}

/* ── 50-state list for the Insured Details tab's State dropdown ── */
export const US_STATES = [
  { name: 'Alabama', code: 'AL' }, { name: 'Alaska', code: 'AK' }, { name: 'Arizona', code: 'AZ' },
  { name: 'Arkansas', code: 'AR' }, { name: 'California', code: 'CA' }, { name: 'Colorado', code: 'CO' },
  { name: 'Connecticut', code: 'CT' }, { name: 'Delaware', code: 'DE' }, { name: 'Florida', code: 'FL' },
  { name: 'Georgia', code: 'GA' }, { name: 'Hawaii', code: 'HI' }, { name: 'Idaho', code: 'ID' },
  { name: 'Illinois', code: 'IL' }, { name: 'Indiana', code: 'IN' }, { name: 'Iowa', code: 'IA' },
  { name: 'Kansas', code: 'KS' }, { name: 'Kentucky', code: 'KY' }, { name: 'Louisiana', code: 'LA' },
  { name: 'Maine', code: 'ME' }, { name: 'Maryland', code: 'MD' }, { name: 'Massachusetts', code: 'MA' },
  { name: 'Michigan', code: 'MI' }, { name: 'Minnesota', code: 'MN' }, { name: 'Mississippi', code: 'MS' },
  { name: 'Missouri', code: 'MO' }, { name: 'Montana', code: 'MT' }, { name: 'Nebraska', code: 'NE' },
  { name: 'Nevada', code: 'NV' }, { name: 'New Hampshire', code: 'NH' }, { name: 'New Jersey', code: 'NJ' },
  { name: 'New Mexico', code: 'NM' }, { name: 'New York', code: 'NY' }, { name: 'North Carolina', code: 'NC' },
  { name: 'North Dakota', code: 'ND' }, { name: 'Ohio', code: 'OH' }, { name: 'Oklahoma', code: 'OK' },
  { name: 'Oregon', code: 'OR' }, { name: 'Pennsylvania', code: 'PA' }, { name: 'Rhode Island', code: 'RI' },
  { name: 'South Carolina', code: 'SC' }, { name: 'South Dakota', code: 'SD' }, { name: 'Tennessee', code: 'TN' },
  { name: 'Texas', code: 'TX' }, { name: 'Utah', code: 'UT' }, { name: 'Vermont', code: 'VT' },
  { name: 'Virginia', code: 'VA' }, { name: 'Washington', code: 'WA' }, { name: 'West Virginia', code: 'WV' },
  { name: 'Wisconsin', code: 'WI' }, { name: 'Wyoming', code: 'WY' }, { name: 'District of Columbia', code: 'DC' },
];
